import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export type PlanTier = 'essentiel' | 'metier' | 'pilotage' | 'daf';

export interface SubscriptionAccess {
  planTier: PlanTier;
  loading: boolean;
  hasActiveSubscription: boolean;
  canAccessBudget: boolean;
  canAccessScenarios: boolean;
  canAccessStressTests: boolean;
  canAccessAdvancedIndicators: boolean;
  maxScenarios: number;
  hasAccess: (feature: string) => boolean;
}

const PLAN_FEATURES: Record<PlanTier, {
  budget: boolean;
  scenarios: boolean;
  maxScenarios: number;
  stressTests: boolean;
  advancedIndicators: boolean;
}> = {
  essentiel: {
    budget: true,
    scenarios: true,
    maxScenarios: 999,
    stressTests: true,
    advancedIndicators: true,
  },
  metier: {
    budget: true,
    scenarios: true,
    maxScenarios: 999,
    stressTests: true,
    advancedIndicators: true,
  },
  pilotage: {
    budget: true,
    scenarios: true,
    maxScenarios: 999,
    stressTests: true,
    advancedIndicators: true,
  },
  daf: {
    budget: true,
    scenarios: true,
    maxScenarios: 999,
    stressTests: true,
    advancedIndicators: true,
  },
};

export function useSubscriptionAccess(): SubscriptionAccess {
  const { user } = useAuth();
  const [planTier, setPlanTier] = useState<PlanTier>('essentiel');
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const timeout = setTimeout(() => {
      console.warn('[useSubscriptionAccess] Loading timeout exceeded, forcing completion');
      setLoading(false);
    }, 3000);

    fetchSubscription().finally(() => clearTimeout(timeout));

    return () => clearTimeout(timeout);
  }, [user]);

  const fetchSubscription = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('trial_ends_at')
        .eq('id', user.id)
        .maybeSingle();

      const trialEndsAt = profile?.trial_ends_at;
      let isTrialActive = false;

      if (trialEndsAt) {
        const trialEndDate = new Date(trialEndsAt);
        const now = new Date();
        isTrialActive = trialEndDate > now;
      }

      if (isTrialActive) {
        setPlanTier('essentiel');
        setHasActiveSubscription(true);
      } else {
        const { data: subscription } = await supabase
          .from('subscriptions')
          .select('plan_type, status')
          .eq('user_id', user.id)
          .maybeSingle();

        if (subscription && subscription.status === 'active') {
          setPlanTier(subscription.plan_type as PlanTier);
          setHasActiveSubscription(true);
        } else {
          setPlanTier('essentiel');
          setHasActiveSubscription(false);
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setPlanTier('essentiel');
    } finally {
      setLoading(false);
    }
  };

  const features = PLAN_FEATURES[planTier];

  const hasAccess = (feature: string): boolean => {
    switch (feature) {
      case 'budget':
        return features.budget;
      case 'scenarios':
        return features.scenarios;
      case 'stress_tests':
        return features.stressTests;
      case 'advanced_indicators':
        return features.advancedIndicators;
      default:
        return false;
    }
  };

  return {
    planTier,
    loading,
    hasActiveSubscription,
    canAccessBudget: features.budget,
    canAccessScenarios: features.scenarios,
    canAccessStressTests: features.stressTests,
    canAccessAdvancedIndicators: features.advancedIndicators,
    maxScenarios: features.maxScenarios,
    hasAccess,
  };
}
